import { useTranslation } from "react-i18next";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { DrawerWrapper } from "@/comps/DrawerWrapper";
import { createNiceModal } from "@/utils/nicemodel";
import { type CountryCodeValue } from "@/main";
import { CountryPicker } from "@/comps/CountryPicker";
import { Button } from "@/comps/Button";
import {
  AdministrativeRegions,
  getRegionsByCountry,
} from "./AdministrativeRegions";
import { RHFWrapper } from "@/common/RHFWrapper";
import { renderAddressString } from "@/utils/renderField";
import { type AddressItem } from "$/utils/fetchGeoData";
import { checkIsJapanResident } from "@/utils/checkIsJapanResident";

export interface AddressInfo {
  residenceCountry: CountryCodeValue | "";
  geoInfos: AddressItem[];
  postcode: string;
  [key: string]: any;
}

export const defaultAddressInfo: AddressInfo = {
  residenceCountry: "",
  geoInfos: [],
  postcode: "",
};

export const openAddressModal = createNiceModal<
  { value?: AddressInfo; autoFillAddress?: AddressInfo },
  { value: AddressInfo }
>(({ _modal, value, autoFillAddress }) => {
  const { t } = useTranslation();
  const [residenceCountry, setResidenceCountry] = useState<CountryCodeValue>();

  const [fillComplete, setFillComplete] = useState(false);

  const { control, handleSubmit, reset, getValues, setValue, clearErrors } =
    useForm<AddressInfo>({
      defaultValues: defaultAddressInfo,
    });

  const resetForm = () => {
    // address字段不确定，所有字段都要清空
    const {
      residenceCountry: currentResidenceCountry,
      geoInfos,
      postcode,
      ...restAddresses
    } = getValues();
    reset({
      residenceCountry: "",
      geoInfos: [],
      postcode: "",
      ...Object.keys(restAddresses).reduce(
        (acc, cur) => ({
          ...acc,
          [cur]: "",
        }),
        {},
      ),
    });
  };
  // TODO: 优化 onClose 跟 useEffect 行为
  // 当 modal 打开时，reset 一次传入的 value，用于首次打开传值的场景
  // 当 modal 关闭时，判断之前是否有有效值，有则reset回去，没有则reset成默认值
  const onClose = () => {
    resetForm();
    _modal.reject();
  };

  // 不会调用触发onClose方法，手动重置下form数据
  const onSubmit = (data: AddressInfo) => {
    // 用 data 拿不到 postcode ?
    // _modal.resolve({ value: data });
    _modal.resolve({ value: getValues() });
    resetForm();
  };

  useEffect(() => {
    if (_modal.props.isOpen) {
      // 每次打开都要判断，重置下初始值
      setFillComplete(false);
      setResidenceCountry(value?.residenceCountry || undefined);
      if (value?.residenceCountry) {
        const addressFieldNames = getRegionsByCountry(value.residenceCountry)
          .map((item) => item.extra.field)
          .filter((n) => n.includes("address"));
        reset({
          residenceCountry: value?.residenceCountry,
          geoInfos: value?.geoInfos,
          postcode: value?.postcode,
          ...addressFieldNames.reduce(
            (acc, cur) => ({
              ...acc,
              [cur]: value?.[cur] || "",
            }),
            {},
          ),
        });
      } else {
        reset(defaultAddressInfo);
      }
    }
  }, [_modal.props.isOpen]);

  return (
    <DrawerWrapper
      {..._modal.props}
      title={t("residential_address")}
      onClose={onClose}
    >
      <div className="px-6 pb-9">
        <form onSubmit={handleSubmit(onSubmit)}>
          {autoFillAddress && !fillComplete ? (
            <div className="bg-color-fill mb-2 w-full rounded-xl px-4 py-3">
              <div className="flex items-center justify-between gap-x-3 leading-6">
                <span>{t("fill_tips")}</span>
                <div
                  className="bg-color-primary hover:bg-color-primary-7 active:bg-color-primary-7 shrink-0 rounded-xl px-3 text-white"
                  onClick={() => {
                    reset(autoFillAddress);
                    setResidenceCountry(
                      autoFillAddress.residenceCountry as CountryCodeValue,
                    );
                    setFillComplete(true);
                  }}
                >
                  {t("fill")}
                </div>
              </div>
              <div className="border-color-border-2 my-2 h-0 w-full border-t border-solid" />
              <div className="wrap-anywhere leading-[22px]">
                {`“${renderAddressString(autoFillAddress, t)}”`}
              </div>
            </div>
          ) : null}
          <RHFWrapper
            name="residenceCountry"
            control={control}
            rules={{ required: t("please_select") }}
          >
            <CountryPicker
              title={t("country_or_region")}
              onChange={(code) => {
                if (code !== residenceCountry) {
                  const addressFieldNames = getRegionsByCountry(code)
                    .map((item) => item.extra.field)
                    .filter((n) => n.includes("address"));
                  reset(
                    // 除国家code，其余的重置成默认值，否则视为undefined
                    // react-hook-form 会认为从非受控变成受控，导致可能引发的异常
                    {
                      residenceCountry: code,
                      geoInfos: [],
                      postcode: "",
                      ...addressFieldNames.reduce(
                        (acc, cur) => ({
                          ...acc,
                          [cur]: "",
                        }),
                        {},
                      ),
                    },
                    { keepIsValid: false },
                  );
                  setResidenceCountry(code);
                }
              }}
              disabled={checkIsJapanResident()}
            />
          </RHFWrapper>
          {residenceCountry && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <AdministrativeRegions
                countryCode={residenceCountry}
                control={control}
                setValue={setValue}
                clearErrors={clearErrors}
              />
            </motion.div>
          )}
          <div className="flex justify-center gap-3 pt-11">
            <Button btnType="outline" onPress={onClose}>
              {t("cancel")}
            </Button>
            <Button type="submit">{t("be_sure")}</Button>
          </div>
        </form>
      </div>
    </DrawerWrapper>
  );
});
